import { useState } from 'react'
import { Channel } from '../../services/channels.service'

const INTERVAL_OPTIONS = [
  { value: 15, label: '15 мин' },
  { value: 30, label: '30 мин' },
  { value: 60, label: '1 час' },
  { value: 120, label: '2 часа' },
  { value: 360, label: '6 часов' },
  { value: 720, label: '12 часов' },
  { value: 1440, label: '24 часа' },
]

interface ChannelCardProps {
  channel: Channel
  onToggleActive: () => void
  onDelete: () => void
  onParse: () => void
  onUpdateAutoParse: (data: { auto_parse_enabled?: boolean; parsing_interval?: number }) => void
  isParsing: boolean
}

export default function ChannelCard({
  channel,
  onToggleActive,
  onDelete,
  onParse,
  onUpdateAutoParse,
  isParsing,
}: ChannelCardProps) {
  const isActive = channel.status === 'monitored'
  const [showSettings, setShowSettings] = useState(false)

  const formatDate = (date: string | null) => {
    if (!date) return 'Никогда'
    return new Date(date).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatNumber = (num: number | null | undefined) => {
    if (num == null) return '—'
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }

  const timeAgo = (date: string | null) => {
    if (!date) return null
    const diff = Date.now() - new Date(date).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'только что'
    if (mins < 60) return `${mins} мин назад`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours} ч назад`
    return `${Math.floor(hours / 24)} д назад`
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-900 truncate">{channel.title}</h3>
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                channel.channel_type === 'group'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-blue-100 text-blue-700'
              }`}
            >
              {channel.channel_type === 'group' ? 'Чат' : 'Канал'}
            </span>
          </div>
          {channel.username && (
            <a
              href={`https://t.me/${channel.username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline"
            >
              @{channel.username}
            </a>
          )}
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={isActive}
            onChange={onToggleActive}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
        <div className="bg-gray-50 rounded p-2">
          <p className="text-gray-500 text-xs">Подписчиков</p>
          <p className="font-medium">{formatNumber(channel.members_count)}</p>
        </div>
        <div className="bg-gray-50 rounded p-2">
          <p className="text-gray-500 text-xs">Последний парсинг</p>
          <p className="font-medium text-xs">{formatDate(channel.last_parsed_at)}</p>
          {channel.last_parsed_at && (
            <p className="text-gray-400 text-[10px]">{timeAgo(channel.last_parsed_at)}</p>
          )}
        </div>
      </div>

      {/* Auto-parse toggle */}
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-2">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={channel.auto_parse_enabled}
              onChange={() =>
                onUpdateAutoParse({ auto_parse_enabled: !channel.auto_parse_enabled })
              }
              disabled={!isActive}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500 peer-disabled:opacity-50"></div>
          </label>
          <span className="text-xs text-gray-600">Авто-парсинг</span>
        </div>

        {channel.auto_parse_enabled && isActive && (
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            {INTERVAL_OPTIONS.find(o => o.value === channel.parsing_interval)?.label || `${channel.parsing_interval} мин`}
            <span className="ml-1">&#9662;</span>
          </button>
        )}
      </div>

      {/* Interval selector (collapsible) */}
      {showSettings && channel.auto_parse_enabled && isActive && (
        <div className="mb-3 px-1">
          <select
            value={channel.parsing_interval}
            onChange={(e) => {
              onUpdateAutoParse({ parsing_interval: Number(e.target.value) })
              setShowSettings(false)
            }}
            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            {INTERVAL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                Каждые {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center pt-3 border-t border-gray-100">
        <button
          onClick={onParse}
          disabled={isParsing || !isActive}
          className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isParsing ? 'Парсинг...' : 'Парсить'}
        </button>
        <button
          onClick={onDelete}
          className="text-sm text-red-600 hover:text-red-800"
        >
          Удалить
        </button>
      </div>
    </div>
  )
}
